/**
 * Back in Stock — Klaviyo Client API + Shopify Horizon
 * ====================================================
 *
 * Teil 1  <back-in-stock>  Der Drawer und die Anmeldung.
 * Teil 2  HorizonBridge    Verdrahtung mit dem Horizon-Variantenwähler:
 *                          Mail-Icon im Größen-Dropdown, Klick auf eine
 *                          ausverkaufte Größe öffnet den Drawer, und der
 *                          Add-to-Cart-Button wird zu „Notify me".
 *
 * Es wird ausschließlich `POST /client/back-in-stock-subscriptions/` aufgerufen.
 * Dieser Endpoint legt ein Profil plus ein Varianten-Abo an — er abonniert KEINE
 * Liste und setzt KEINEN E-Mail-Marketing-Consent. Deshalb gibt es kein
 * Double-Opt-in und keine Bestätigungsmail. Wer `consent` oder eine
 * `list`-Relationship in den Payload ergänzt, holt sich das Double-Opt-in zurück.
 *
 * Alle Texte für Kundinnen und Kunden stehen in TEXT und sind englisch.
 */
(() => {
  const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const REVISION = '2024-05-15';
  const STORAGE_KEY = 'bis:subscribed';

  /**
   * Bewusst leer. U+2709 (✉) hat eine Emoji-Darstellung: Windows zeigt die einfarbige
   * Textvariante, macOS greift trotz Variation Selector 15 zu Apple Color Emoji.
   * Dagegen hilft kein CSS — den Text einer <option> zeichnet auf dem Mac das
   * Betriebssystem, `font-family` auf <option> wirkt dort nicht. Ein garantiert
   * einfarbiges Briefsymbol gibt es in Unicode nicht.
   * Das echte SVG-Icon sitzt auf dem Notify-Button, dort ist es steuerbar.
   * Auf einen Textmarker umstellen geht hier: z.B. '›' oder '•'.
   */
  const MAIL_GLYPH = '';

  const TEXT = {
    emailMissing: 'Please enter a valid email address.',
    success: "You're on the list — we'll email you as soon as it's back.",
    already: "You're already signed up for this size.",
    error: 'Something went wrong. Please try again later.',
    sending: 'Sending…',
    notifyMe: 'Notify me',
    // Der Zusatz in den Optionen hängt davon ab, was der Button gerade sagt:
    //   Button = ADD TO CART  → "Notify me", sonst wüsste niemand, dass es die Funktion gibt
    //   Button = NOTIFY ME    → "Sold out",  sonst stünde dieselbe Aufforderung bei einem
    //                           komplett ausverkauften Produkt sechsmal auf einem Bildschirm
    optionSuffixNotify: '— Notify me',
    optionSuffixSoldOut: '— Sold out',
    // Produkt mit "Coming Soon" getaggt: der Zustand ist nicht "ausverkauft", sondern
    // "kommt noch". Schlägt beide anderen Zusätze. Bewusst ohne "Notify me" —
    // die Aufforderung steht auf dem Button.
    optionSuffixComingSoon: '— Coming soon',
  };

  /* ==================================================================== */
  /* Teil 1 — der Drawer                                                  */
  /* ==================================================================== */

  class BackInStock extends HTMLElement {
    #dialog;
    #form;
    #select;
    #email;
    #submit;
    #submitLabel;
    #status;
    #opener = null;
    #defaultLabel = '';

    connectedCallback() {
      // Horizon rendert die Section `product-information` bei jedem Variantenwechsel neu —
      // und der Drawer geht ja gerade DURCH einen Variantenwechsel auf. Damit er dabei nicht
      // angefasst wird, zieht er einmalig an <body> um und steht danach außerhalb der Section.
      // Das Umhängen löst connectedCallback erneut aus, dann greift der Zweig unten.
      if (this.parentElement !== document.body) {
        const existing = document.querySelector(
          `body > back-in-stock[data-product-id="${this.dataset.productId}"]`
        );
        // Nach einem Re-Render liefert der Server das Snippet erneut aus. Dann gibt es den
        // Drawer schon an <body> — die frische Kopie entfernt sich, statt sich zu verdoppeln.
        if (existing && existing !== this) {
          this.remove();
          return;
        }
        document.body.appendChild(this);
        return;
      }

      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.#dialog = this.querySelector('dialog');
      this.#form = this.querySelector('.bis-form');
      this.#select = this.querySelector('[data-bis-variant]');
      this.#email = this.querySelector('[data-bis-email]');
      this.#submit = this.querySelector('[data-bis-submit]');
      this.#submitLabel = this.querySelector('[data-bis-submit-label]');
      this.#status = this.querySelector('[data-bis-status]');

      if (!this.#dialog || !this.#form || !this.#select || !this.#email) return;

      this.#defaultLabel = this.#submitLabel?.textContent.trim() ?? TEXT.notifyMe;

      this.#form.addEventListener('submit', this.#onSubmit);
      this.#email.addEventListener('input', this.#clearError);
      this.#select.addEventListener('change', this.#onVariantChange);

      this.querySelector('[data-bis-close]')?.addEventListener('click', () => this.close());
      this.#dialog.addEventListener('click', this.#onBackdropClick);
      this.#dialog.addEventListener('close', this.#onClose);

      document.addEventListener('click', this.#onDocumentClick);

      new HorizonBridge(this).start();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocumentClick);
    }

    /** Alle ausverkauften Varianten-IDs dieses Produkts. */
    get unavailableIds() {
      return new Set([...(this.#select?.options ?? [])].map((option) => option.value));
    }

    /* ---------------------------------------------------------- öffnen */

    open(variantId, opener) {
      if (opener) this.#opener = opener;

      const wanted = String(variantId ?? this.#detectSelectedVariantId() ?? '');
      if (wanted && this.#hasOption(wanted)) this.#select.value = wanted;

      this.#setStatus('', null);
      this.#clearError();
      this.#onVariantChange();

      if (!this.#dialog.open) this.#dialog.showModal();
      document.body.style.overflow = 'hidden';
      this.#email.focus({ preventScroll: true });
    }

    close() {
      this.#dialog.close();
    }

    #onDocumentClick = (event) => {
      const trigger = event.target.closest?.('[data-bis-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger.dataset.variantId, trigger);
    };

    #onBackdropClick = (event) => {
      if (event.target === this.#dialog) this.close();
    };

    #onClose = () => {
      document.body.style.overflow = '';
      this.#opener?.focus?.({ preventScroll: true });
      this.#opener = null;
    };

    /* ------------------------------------------------------- Varianten */

    #hasOption(variantId) {
      return [...this.#select.options].some((option) => option.value === variantId);
    }

    /**
     * Themeunabhängige Ermittlung der aktuell gewählten Variante: das Cart-Formular
     * führt in jedem Theme ein <input name="id"> mit der Varianten-ID.
     */
    #detectSelectedVariantId() {
      const input = document.querySelector(
        'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
      );
      return input?.value || null;
    }

    #onVariantChange = () => {
      if (this.#isSubscribed(this.#select.value)) {
        this.#setStatus(TEXT.already, 'success');
      } else if (this.#status?.dataset.state === 'success') {
        this.#setStatus('', null);
      }
    };

    /* --------------------------------------------------------- Absenden */

    #onSubmit = async (event) => {
      event.preventDefault();

      const email = this.#email.value.trim();
      const variantId = this.#select.value;

      if (!email || !this.#email.checkValidity()) {
        this.#email.setAttribute('aria-invalid', 'true');
        this.#email.focus();
        this.#setStatus(TEXT.emailMissing, 'error');
        return;
      }

      this.#setBusy(true);
      this.#setStatus(TEXT.sending, null);

      try {
        const response = await fetch(
          `${ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: REVISION },
            body: JSON.stringify(this.#payload(email, variantId)),
          }
        );

        if (response.status === 202) {
          this.#remember(variantId);
          this.#setStatus(TEXT.success, 'success');
          this.#email.value = '';
        } else {
          this.#setStatus(await this.#errorMessage(response), 'error');
        }
      } catch {
        this.#setStatus(TEXT.error, 'error');
      } finally {
        this.#setBusy(false);
      }
    };

    #payload(email, variantId) {
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: `${this.dataset.catalog}${variantId}` },
            },
          },
        },
      };
    }

    async #errorMessage(response) {
      try {
        const body = await response.json();
        return body?.errors?.[0]?.detail || TEXT.error;
      } catch {
        return TEXT.error;
      }
    }

    /* ------------------------------------------------------------ State */

    #setBusy(busy) {
      if (!this.#submit) return;
      this.#submit.disabled = busy;
      if (this.#submitLabel) {
        this.#submitLabel.textContent = busy ? TEXT.sending : this.#defaultLabel;
      }
    }

    #setStatus(message, state) {
      if (!this.#status) return;
      this.#status.textContent = message;
      if (state) this.#status.dataset.state = state;
      else delete this.#status.dataset.state;
    }

    #clearError = () => {
      this.#email.removeAttribute('aria-invalid');
      if (this.#status?.dataset.state === 'error') this.#setStatus('', null);
    };

    /* ----------------------------- „bereits angemeldet"-Merker (Komfort) */

    #stored() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      } catch {
        return [];
      }
    }

    #isSubscribed(variantId) {
      return this.#stored().includes(String(variantId));
    }

    #remember(variantId) {
      try {
        const list = this.#stored();
        if (!list.includes(String(variantId))) list.push(String(variantId));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-50)));
      } catch {
        /* Private Mode o.ä. — der Merker ist reiner Komfort */
      }
    }
  }

  /* ==================================================================== */
  /* Teil 2 — Brücke zum Horizon-Variantenwähler                          */
  /* ==================================================================== */

  class HorizonBridge {
    #bis;
    #applying = false;
    #scheduled = false;
    #observer;

    constructor(backInStockElement) {
      this.#bis = backInStockElement;
    }

    start() {
      this.#apply();

      // Horizon tauscht bei jedem Variantenwechsel das <select> aus und
      // aktualisiert den Add-to-Cart-Button. Beides muss danach neu gesetzt werden.
      //
      // Bewusst setTimeout statt requestAnimationFrame: rAF läuft nicht, solange der
      // Tab nicht gezeichnet wird (Hintergrund-Tab, Prerender) — die Dekoration würde
      // dort ausbleiben. Und bewusst ein `scheduled`-Flag statt clearTimeout, damit
      // ein dauerhaft mutierender Seitenbereich den Lauf nicht endlos verschiebt.
      this.#observer = new MutationObserver(() => {
        if (this.#applying || this.#scheduled) return;
        this.#scheduled = true;
        setTimeout(() => {
          this.#scheduled = false;
          this.#apply();
        }, 0);
      });
      this.#observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'value'],
      });

      // Auswahl einer ausverkauften Größe: delegiert, weil das <select> ersetzt wird.
      document.addEventListener('change', this.#onSelectChange, true);
    }

    #apply() {
      this.#applying = true;
      try {
        this.#decorateOptions();
        this.#syncAddToCart();
      } finally {
        this.#applying = false;
      }
    }

    /* ------------------------------ Mail-Icon im Größen-Dropdown */

    /** Ist die gerade gewählte Variante ausverkauft? Dann steht auf dem Button „Notify me". */
    #currentVariantSoldOut() {
      const id = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      return !!id && this.#bis.unavailableIds.has(id);
    }

    #decorateOptions() {
      const unavailable = this.#bis.unavailableIds;

      // "Coming Soon" schlägt alles: das Produkt ist nicht ausverkauft, es kommt erst.
      // Sonst gilt: sagt der Button schon „Notify me", genügt in den Optionen der Zustand.
      // Sagt er „Add to cart", ist der Zusatz der einzige Hinweis auf die Funktion.
      let mode;
      if (this.#bis.dataset.comingSoon === 'true') {
        mode = 'coming';
      } else {
        mode = this.#currentVariantSoldOut() ? 'soldout' : 'notify';
      }

      const suffix = {
        coming: TEXT.optionSuffixComingSoon,
        soldout: TEXT.optionSuffixSoldOut,
        notify: TEXT.optionSuffixNotify,
      }[mode];

      document.querySelectorAll('select.variant-option__select option').forEach((option) => {
        const variantId = option.dataset.variantId;
        if (!variantId) return;

        const shouldMark = unavailable.has(variantId);
        const marked = option.dataset.bis; // undefined | 'notify' | 'soldout'

        // Nichts zu tun, wenn die Option bereits im richtigen Modus beschriftet ist.
        if (shouldMark ? marked === mode : !marked) return;

        if (shouldMark) {
          if (!marked) option.dataset.bisOriginal = option.textContent;
          option.dataset.bis = mode;
          // `option.value` ist der saubere Optionswert („28"), unabhängig davon,
          // welchen Ausverkauft-Zusatz das Theme in den Text schreibt.
          // Leere Teile fallen raus, damit ohne Icon kein führendes Leerzeichen bleibt.
          option.textContent = [MAIL_GLYPH, option.value, suffix].filter(Boolean).join(' ');
        } else {
          if (option.dataset.bisOriginal) option.textContent = option.dataset.bisOriginal;
          delete option.dataset.bis;
          delete option.dataset.bisOriginal;
        }
      });
    }

    #onSelectChange = (event) => {
      const select = event.target;
      if (!select?.classList?.contains('variant-option__select')) return;

      const option = select.selectedOptions?.[0];
      const variantId = option?.dataset.variantId;
      if (!variantId || !this.#bis.unavailableIds.has(variantId)) return;

      this.#bis.open(variantId, select);
    };

    /* ------------------------- Add-to-Cart-Button → „Notify me" */

    #syncAddToCart() {
      const unavailable = this.#bis.unavailableIds;

      document.querySelectorAll('add-to-cart-component').forEach((component) => {
        const form = component.closest('form[action*="/cart/add"]');
        const currentId = form?.querySelector('input[name="id"]')?.value;
        const soldOut = !!currentId && unavailable.has(currentId);

        component.classList.toggle('bis-hidden', soldOut);

        let button = component.parentElement?.querySelector(':scope > .bis-atc');
        if (soldOut && !button) {
          button = this.#buildButton(component);
          component.after(button);
        }
        if (button) {
          button.hidden = !soldOut;
          button.dataset.variantId = currentId ?? '';
        }
      });
    }

    #buildButton(component) {
      const themeButton = component.querySelector('button[name="add"]');
      const button = document.createElement('button');

      button.type = 'button';
      // Die Klassen des echten Buttons übernehmen, damit er identisch aussieht.
      button.className = `${themeButton?.className ?? 'button'} bis-atc`;
      button.dataset.bisOpen = '';
      button.setAttribute('aria-haspopup', 'dialog');
      button.innerHTML = `
        <span class="add-to-cart-text">
          <span aria-hidden="true" class="svg-wrapper bis-atc__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20" fill="none">
              <rect x="2.5" y="4.5" width="15" height="11" rx="1.5"
                stroke="currentColor" stroke-width="var(--icon-stroke-width, 1)" />
              <path d="m3 6 7 5 7-5" stroke="currentColor"
                stroke-width="var(--icon-stroke-width, 1)" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="add-to-cart-text__content"><span><span>${TEXT.notifyMe}</span></span></span>
        </span>`;

      return button;
    }
  }

  if (!customElements.get('back-in-stock')) {
    customElements.define('back-in-stock', BackInStock);
  }
})();